import { JSX, useEffect, useState } from "react";
import { useAuthService } from "./useAuthService";
import {
  type AuthTokens,
  clearTokensLocalStorage,
  getLocalToken,
} from "./LocalStorage";
import { useHandlePermissions } from "@/shared/stores/permissionsStore";
import axios from "axios";
import { useLoadingStore } from "../shared/stores";

interface IAuthComponent {
  children: JSX.Element;
}

const AuthComponent = ({ children }: IAuthComponent) => {
  const { getTokens, validateResource, redirectToLogin, refreshTokens } =
    useAuthService();
  const { handlePermissions, clearPermissions } = useHandlePermissions();
  // Flag global que controla el spinner (visible para toda la app)
  const { start, stop } = useLoadingStore();

  // Flag local que solo controla el renderizado de los hijos (invisible para el usuario)
  const [loadingCheckAuth, setLoadingCheckAuth] = useState<boolean>(true);

  const clearSessionAndRedirect = () => {
    clearTokensLocalStorage();
    clearPermissions();
    redirectToLogin();
  };

  const validateAuthentication = async (tokens: AuthTokens) => {
    const checkResource = await validateResource(null, tokens);

    if (checkResource?.data) {
      handlePermissions(checkResource.data);
    }
  };

  const validateWithSingleRefresh = async (tokens: AuthTokens) => {
    try {
      await validateAuthentication(tokens);
    } catch (error: unknown) {
      const isUnauthorized =
        axios.isAxiosError(error) && error.response?.status === 401;

      if (!isUnauthorized) throw error;

      const refreshedTokens = await refreshTokens(tokens);
      await validateAuthentication(refreshedTokens);
    }
  };

  const checkOrGetToken = async () => {
    start("bootstrap");
    setLoadingCheckAuth(true);

    try {
      const urlParams = new URLSearchParams(window.location.search);
      const code: string | null = urlParams.get("code");

      // Leer token actual (fresh read)
      let localToken = getLocalToken();

      if (code) {
        // Si el SSO nos devolvió con un code, siempre usarlo (token fresco)
        clearTokensLocalStorage();
        localToken = await getTokens(code);
      }

      if (!localToken?.accessToken) {
        // No hay token válido → redirigir al login sin montar children
        clearSessionAndRedirect();
        return;
      }

      // Validar y, solo ante un 401, renovar una única vez
      await validateWithSingleRefresh(localToken);

      // Limpiar el parámetro "code" de la URL: si no quedan params, no dejamos el '?'
      if (code) {
        // Si venimos del login (hay code), ir al home
        window.history.replaceState({}, document.title, "/");
      } else {
        // Solo limpiar parámetros si no hay code
        urlParams.delete("code");
        const paramsString = urlParams.toString();
        const newUrl = paramsString
          ? `${window.location.pathname}?${paramsString}`
          : window.location.pathname;
        window.history.replaceState({}, document.title, newUrl);
      }

      // Solo desbloquear children si auth fue exitoso
      stop("bootstrap");
      setLoadingCheckAuth(false);
    } catch {
      clearSessionAndRedirect();
    }
  };

  useEffect(() => {
    checkOrGetToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loadingCheckAuth) {
    return null;
  }

  return children;
};

export default AuthComponent;
